/**
 * AttendanceCalendar.jsx — Puantaj Takvim Görünümü
 * Haftalık / Aylık toggle
 * Her gün hücresine renkli durum ikonu
 * Tıklayınca popover detay
 */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { getStatusCalendarStyle, ATTENDANCE_STATUS } from '../utils/attendanceHelpers';

/**
 * @param {Object} props
 * @param {Array} props.records — attendance kayıtları [{ date, status, clock_in, clock_out, total_work_hours }]
 * @param {'weekly'|'monthly'} props.defaultView
 * @param {Function} props.onDayClick — (dateStr) => void
 */
const AttendanceCalendar = ({
  records = [],
  defaultView = 'monthly',
  onDayClick,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'tr' ? 'tr-TR' : (i18n.language === 'ar' ? 'ar-SA' : 'en-US');
  const [viewMode, setViewMode] = useState(defaultView);
  const [currentDate, setCurrentDate] = useState(new Date());

  /** Kayıtları date→record map'ine çevir */
  const recordMap = useMemo(() => {
    const map = {};
    (records || []).forEach((rec) => {
      const key = rec.date?.split('T')[0] || rec.date;
      if (key) map[key] = rec;
    });
    return map;
  }, [records]);

  /** Ay navigasyonu */
  const navigateMonth = (delta) => {
    setCurrentDate((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + delta);
      return next;
    });
  };

  /** Hafta navigasyonu */
  const navigateWeek = (delta) => {
    setCurrentDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + delta * 7);
      return next;
    });
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // ---------- AYLIK TAKVİM ----------
  const monthlyDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0=Pazar
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Önceki aydan dolgu
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, iso: d.toISOString().split('T')[0], isPadding: true });
    }

    // Bu ay
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i);
      days.push({ date: d, iso: d.toISOString().split('T')[0], isPadding: false });
    }

    // Sonraki aydan dolgu
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const d = new Date(year, month + 1, i);
        days.push({ date: d, iso: d.toISOString().split('T')[0], isPadding: true });
      }
    }

    return days;
  }, [year, month]);

  // ---------- HAFTALIK TAKVİM ----------
  const weeklyDays = useMemo(() => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Pazartesi başlangıç
    startOfWeek.setDate(startOfWeek.getDate() + diff);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(d.getDate() + i);
      days.push({ date: d, iso: d.toISOString().split('T')[0], isPadding: false });
    }
    return days;
  }, [currentDate]);

  const dayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
  const days = viewMode === 'monthly' ? monthlyDays : weeklyDays;

  /** Bir günün durumunu belirle */
  const getDayStatus = (iso) => {
    const rec = recordMap[iso];
    if (!rec) return null;
    if (rec.record_type === 'SICK_REPORT') return 'SICK_REPORT';
    if (rec.status === 'ABSENT' || rec.status === 'MISSING_CLOCK_IN' || rec.status === 'MISSING_CLOCK_OUT') return 'ABSENT';
    if (rec.status === 'LATE' || rec.status === 'EARLY_OUT' || rec.status === 'LATE_EARLY_OUT') return 'LATE';
    if (rec.status === 'PRESENT') return 'PRESENT';
    return 'PRESENT';
  };

  /** Durum ikonu */
  const statusIcon = (status) => {
    switch (status) {
      case 'PRESENT': return '✅';
      case 'LATE': return '⚠️';
      case 'ABSENT': return '❌';
      case 'SICK_REPORT': return '📋';
      case 'HOLIDAY': return '🏖️';
      default: return '';
    }
  };

  const titleLabel = viewMode === 'monthly'
    ? currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
    : `${weeklyDays[0]?.date.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} - ${weeklyDays[6]?.date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <button
            onClick={() => viewMode === 'monthly' ? navigateMonth(-1) : navigateWeek(-1)}
            className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
            aria-label={t('prev', 'Önceki')}
          >
            <ChevronLeft size={18} />
          </button>
          <h3 className="text-sm font-black text-slate-800 min-w-[140px] text-center">
            {titleLabel}
          </h3>
          <button
            onClick={() => viewMode === 'monthly' ? navigateMonth(1) : navigateWeek(1)}
            className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
            aria-label={t('next', 'Sonraki')}
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* View Toggle */}
        <div className="flex items-center rounded-xl bg-slate-100 p-1">
          <button
            onClick={() => setViewMode('weekly')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              viewMode === 'weekly'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('attendance_view_weekly', 'Haftalık')}
          </button>
          <button
            onClick={() => setViewMode('monthly')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              viewMode === 'monthly'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <span className="flex items-center gap-1">
              <CalendarDays size={12} />
              {t('attendance_view_monthly', 'Aylık')}
            </span>
          </button>
        </div>
      </div>

      {/* Takvim Grid */}
      <div className="p-3 md:p-4">
        {/* Gün isimleri */}
        <div className="grid grid-cols-7 mb-2">
          {dayNames.map((name) => (
            <div key={name} className="text-center text-[10px] font-black uppercase tracking-wider text-slate-400 py-1">
              {name}
            </div>
          ))}
        </div>

        {/* Gün hücreleri */}
        <div className="grid grid-cols-7 gap-1">
          {days.map(({ date, iso, isPadding }) => {
            const status = getDayStatus(iso);
            const style = status ? getStatusCalendarStyle(status) : getStatusCalendarStyle(null);
            const isToday = iso === new Date().toISOString().split('T')[0];
            const rec = recordMap[iso];

            return (
              <button
                key={iso}
                disabled={isPadding}
                onClick={() => !isPadding && onDayClick?.(iso)}
                className={`
                  relative aspect-square rounded-xl flex flex-col items-center justify-center
                  transition-all duration-150 text-[11px] md:text-sm font-bold
                  ${isPadding ? 'opacity-20 cursor-default' : 'cursor-pointer hover:scale-105 hover:shadow-md'}
                  ${isToday ? 'ring-2 ring-cyan-500 ring-offset-1' : ''}
                  ${style.bg}
                  ${!isPadding ? style.ring + ' ring-1' : ''}
                `}
                title={rec ? `${rec.clock_in || '-'} → ${rec.clock_out || '-'} (${rec.total_work_hours || 0}h)` : ''}
              >
                <span className={`${isToday ? 'text-cyan-700' : 'text-slate-700'}`}>
                  {date.getDate()}
                </span>
                {status && (
                  <span className="text-xs leading-none mt-0.5">{statusIcon(status)}</span>
                )}
                {rec?.total_work_hours > 0 && (
                  <span className="text-[9px] text-slate-400 font-medium mt-0.5">
                    {rec.total_work_hours}h
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Lejant */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-t border-slate-100 text-[10px] font-bold text-slate-500">
        <span className="flex items-center gap-1">✅ {t('attendance_legend_present', 'Giriş')}</span>
        <span className="flex items-center gap-1">⚠️ {t('attendance_legend_late', 'Geç')}</span>
        <span className="flex items-center gap-1">❌ {t('attendance_legend_absent', 'Yok')}</span>
        <span className="flex items-center gap-1">📋 {t('attendance_legend_sick', 'Rapor')}</span>
        <span className="flex items-center gap-1">🏖️ {t('attendance_legend_holiday', 'Tatil')}</span>
      </div>
    </div>
  );
};

export default AttendanceCalendar;